import { getString } from "@/common/uistringlabels/ui-string-utils";

/**
 * To add, remove, or reorder an option, edit this array only — the
 * {@link CampaignResponseValue} type and every consumer derive from it
 * automatically. `labelKey` and optional `descriptionKey` are keys into
 * `campaigns.responseOptions` in `uistrings.json`.
 */
const RESPONSE_OPTION_CONFIG = [
  { value: "current-device", labelKey: "currentDevice" },
  { value: "testing-device", labelKey: "testingDevice" },
  { value: "already-returned", labelKey: "alreadyReturned" },
  { value: "return-pickup", labelKey: "returnPickup" },
  { value: "return-dropoff", labelKey: "returnDropoff" },
  { value: "dont-have", labelKey: "dontHave" },
  { value: "lost-or-stolen", labelKey: "lostOrStolen", areRemarksRequired: true },
  { value: "cico-laptop", labelKey: "cicoLaptop", descriptionKey: "cicoLaptopDescription" },
  { value: "byod-device", labelKey: "byodDevice", descriptionKey: "byodDeviceDescription" },
  { value: "shared-device", labelKey: "sharedDevice" },
] as const satisfies readonly {
  value: string;
  labelKey: string;
  descriptionKey?: string;
  areRemarksRequired?: boolean;
}[];

/** The empty string represents "no response selected yet". */
export type CampaignResponseValue = (typeof RESPONSE_OPTION_CONFIG)[number]["value"] | "";

/** The one response value that requires a pickup address. */
export const RETURN_PICKUP_RESPONSE: CampaignResponseValue = "return-pickup";

const REMARKS_REQUIRED_RESPONSES: ReadonlySet<CampaignResponseValue> = new Set(
  RESPONSE_OPTION_CONFIG.filter(
    (option) => "areRemarksRequired" in option && option.areRemarksRequired
  ).map((option) => option.value)
);

/** HSS refuses these responses unless they carry non-blank remarks. */
export function areRemarksRequiredFor(value: CampaignResponseValue): boolean {
  return REMARKS_REQUIRED_RESPONSES.has(value);
}

export interface CampaignResponseOption {
  value: CampaignResponseValue;
  label: string;
  description?: string;
}

export const RESPONSE_OPTIONS: CampaignResponseOption[] = RESPONSE_OPTION_CONFIG.map((option) => ({
  value: option.value,
  label: getString(`campaigns.responseOptions.${option.labelKey}`),
  ...("descriptionKey" in option
    ? { description: getString(`campaigns.responseOptions.${option.descriptionKey}`) }
    : {}),
}));
